import NavigationDrawer from "../NavigationDrawer";
import NavigationMenu from "../NavigationMenu";
import { faBars } from "@/icons/regular";
import { cn } from "@/utils/cnHelper";
import { Button, Icon } from "@ui";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import * as React from "react";

type LinkConfig = {
  ctaText: string;
  ctaLink: string;
  signInText: string;
  signInLink: string;
};

const DEFAULT_CTA_TEXT = "Start creating";
const DEFAULT_CTA_LINK = "https://app.shorthand.com/signup";
const DEFAULT_SIGNIN_TEXT = "Sign in";
const DEFAULT_SIGNIN_LINK = "https://app.shorthand.com/signin";

const Logomark = ({ theme }: { theme: "dark-theme" | "light-theme" | "transparent-dark-theme" }) => {
  return (
    <Link to={"/"} aria-label="Shorthand homepage">
      <span className="tw-sr-only">Shorthand</span>
      {theme === "light-theme" ? (
        <StaticImage
          src="../../../../static/media/logos/logo-black-horiz.svg"
          alt="Shorthand Logo"
          aria-hidden="true"
          className="tw-h-auto tw-w-[120px]"
          placeholder="blurred"
        />
      ) : (
        <StaticImage
          src="../../../../static/shorthand/logotype-horizontal.svg"
          alt="Shorthand Logo"
          aria-hidden="true"
          className="tw-h-auto tw-w-[120px]"
          placeholder="none"
        />
      )}
    </Link>
  );
};

export default function Header({
  linkConfig,
  onSalesClick,
  theme = "dark-theme",
}: {
  linkConfig?: LinkConfig;
  onSalesClick: () => void;
  theme?: "dark-theme" | "light-theme" | "transparent-dark-theme";
}) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  return (
    <header
      className={cn(
        "tw-sticky tw-inset-x-0 tw-top-0 tw-z-50 tw-backdrop-blur-sm",
        theme === "light-theme" ? "tw-bg-[var(--header-background-color-alt)]" : "tw-bg-[var(--header-background-color)]"
      )}
    >
      <nav aria-label="Global" className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2">
        <div className="tw-flex tw-gap-3 lg:tw-flex-1">
          <Logomark theme={theme} />
          <div className="tw-hidden tw-h-auto tw-w-px tw-border-r tw-border-grey_scale-200/50 lg:tw-block" />
          <div className="tw-hidden lg:tw-flex lg:tw-gap-x-12">
            <NavigationMenu />
          </div>
        </div>
        <div className="tw-flex lg:tw-hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="tw--m-2.5 tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-p-2.5 tw-text-white"
          >
            <span className="tw-sr-only">Open main menu</span>
            <Icon icon={faBars} />
          </button>
        </div>
        <div className="tw-hidden lg:tw-flex lg:tw-flex-1 lg:tw-justify-end lg:tw-gap-2">
          <Button asChild variant="primary-gradient" size="sm" className="tw-uppercase">
            <a href={linkConfig?.ctaLink || DEFAULT_CTA_LINK} target="_blank" className="tw-no-underline hover:tw-no-underline">
              {linkConfig?.ctaText || DEFAULT_CTA_TEXT}
            </a>
          </Button>
          <Button
            variant="secondary-outline"
            size="sm"
            className="tw-uppercase"
            style={{ border: "1px solid white" }}
            onClick={onSalesClick}
          >
            Contact sales
          </Button>
          <Button asChild variant="secondary-outline" size="sm" className="tw-uppercase">
            <a href={linkConfig?.signInLink || DEFAULT_SIGNIN_LINK} target="_blank" className="tw-no-underline hover:tw-no-underline">
              {linkConfig?.signInText || DEFAULT_SIGNIN_TEXT}
            </a>
          </Button>
        </div>
      </nav>
      <NavigationDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onSalesClick={onSalesClick}
        linkConfig={{
          ctaText: linkConfig?.ctaText || DEFAULT_CTA_TEXT,
          ctaLink: linkConfig?.ctaLink || DEFAULT_CTA_LINK,
          signInText: linkConfig?.signInText || DEFAULT_SIGNIN_TEXT,
          signInLink: linkConfig?.signInLink || DEFAULT_SIGNIN_LINK,
        }}
      />
    </header>
  );
}
